/**
 * The debug renderer: the character's collision capsule, arms, reach, pendulum,
 * and every timing window as boxes and lines, built before any silhouette so
 * feel is judged without art, plus the gym's teaching marks. The world itself
 * (blocks, water, bars, the start and finish) is `WorldRenderer`. Every graphics
 * object is created once and updated in place; nothing is allocated per frame.
 */

import {
  type CompiledTuning,
  Grade,
  HZ,
  restLength,
  rollThreshold,
  S,
  type Sim,
  SolidClass,
  vaultTakeoffTime,
  type World,
} from "@lache/sim";
import { Container, type Sprite } from "pixi.js";
import type { Camera } from "./camera";
import type { RenderFeel } from "./feel";
import { NEON } from "./palette";
import {
  type CapsuleShape,
  makeCapsule,
  type ShapeFactory,
  setCapsule,
  setCapsuleTint,
  setDisc,
  setLine,
} from "./shapes";
import type { ViewState } from "./view";

/** The debug shapes' colors on the night world (the world's own colors are `NEON` in palette.ts). */
export const COLORS = {
  background: NEON.night,
  capsule: NEON.paper,
  capsuleFill: 0x1a0a3a,
  com: NEON.pink,
  facing: NEON.paper,
  arm: NEON.paper,
  armOut: NEON.pink,
  reach: NEON.pink,
  assist: NEON.yellow,
  pendulum: NEON.cyan,
  marker: NEON.cyan,
  perfect: NEON.green,
  good: NEON.yellow,
  mark: NEON.dim,
  respawn: NEON.green,
  flash: 0xffffff,
  water: NEON.water,
} as const;

const ARC_SEGMENTS = 20;

export class DebugRenderer {
  readonly root = new Container();
  /** The gym's teaching marks (the vault and slide windows, split marks, respawn points). */
  private readonly tells = new Container();
  private readonly dynamic = new Container();
  private readonly capsule: CapsuleShape;
  private readonly capsuleInner: CapsuleShape;
  private readonly com: Sprite;
  private readonly facingTick: Sprite;
  private readonly armLeft: Sprite;
  private readonly armRight: Sprite;
  private readonly reachRing: Sprite;
  private readonly assistLine: Sprite;
  private readonly pendulum: Sprite;
  private readonly bottomMarker: Sprite;
  private readonly arcGood: Sprite[] = [];
  private readonly arcPerfect: Sprite[] = [];
  /** Release wedges past the bottom on the forward side of the attached bar. */
  private readonly releaseGood: Sprite[] = [];
  private readonly releasePerfect: Sprite[] = [];
  /** Catch-ready zone behind the next bar: where the mass must hang at the grab to start a forward swing. */
  private readonly readyZone: Sprite[] = [];
  /** Roll tell while airborne: the forward spin against the spin the legs absorb at this tuck. */
  private readonly rollTrack: Sprite;
  private readonly rollSpin: Sprite;
  private readonly rollMark: Sprite;
  private readonly flash: Sprite;

  constructor(f: ShapeFactory, world: World, t: CompiledTuning) {
    this.root.addChild(this.tells);
    this.root.addChild(this.dynamic);

    for (let i = 0; i < world.solidCount; i++) {
      const x0 = world.solidX0[i]!;
      const y0 = world.solidY0[i]!;
      const w = world.solidX1[i]! - x0;
      const h = world.solidY1[i]! - y0;
      const cls = world.solidClass[i];
      // Windows on the ground before a box (the vault) and a pipe (the slide), in meters at max run
      // speed for travel to the right: GOOD, PERFECT inside it, and the automatic plant line.
      const v = t.maxRunSpeed / 1000;
      const face = x0 - t.capsuleRadius;
      if (cls === SolidClass.Box && h > t.hurdleStepHeight && h <= t.hurdleMaxHeight) {
        // The press window is measured back from the takeoff point (the red tick).
        const takeoffX = face - vaultTakeoffTime(0, t, h) * 1000 * v;
        const g0 = takeoffX - t.vaultWindowMs * v;
        f.rect(this.tells, g0, y0 - 0.03, takeoffX - g0, 0.06, COLORS.good, 0.8);
        const p0 = takeoffX - (t.vaultPerfectLeadMs + t.vaultPerfectHalfMs) * v;
        f.rect(this.tells, p0, y0 - 0.03, 2 * t.vaultPerfectHalfMs * v, 0.06, COLORS.perfect, 0.9);
        f.rect(this.tells, takeoffX - 0.01, y0 - 0.03, 0.02, 0.2, COLORS.com, 0.9);
      } else if (cls === SolidClass.Pipe) {
        const ground = world.probeDown(x0, x0 + w, y0, 3);
        const gy = ground > Number.NEGATIVE_INFINITY ? ground : y0;
        const g0 = face - t.slideWindowMs * v;
        const g1 = face - t.slideWindowLateMs * v;
        f.rect(this.tells, g0, gy - 0.03, g1 - g0, 0.06, COLORS.good, 0.8);
        const p0 = face - (t.slidePerfectMs + t.slidePerfectHalfMs) * v;
        f.rect(this.tells, p0, gy - 0.03, 2 * t.slidePerfectHalfMs * v, 0.06, COLORS.perfect, 0.9);
      }
    }
    for (let i = 0; i < world.markCount; i++) {
      const x = world.markX[i]!;
      for (let k = 0; k < 10; k++) f.rect(this.tells, x - 0.01, k * 0.6, 0.02, 0.3, COLORS.mark);
    }
    for (let i = 0; i < world.respawnCount; i++) {
      const x = world.respawnX[i]!;
      const y = world.respawnY[i]!;
      f.rect(this.tells, x - 0.15, y - 0.06, 0.3, 0.06, COLORS.respawn);
    }

    this.pendulum = f.line(this.dynamic, COLORS.pendulum, 0.03, 0.8);
    this.bottomMarker = f.rect(this.dynamic, 0, 0, 0.04, 0.25, COLORS.marker);
    for (let i = 0; i < ARC_SEGMENTS; i++) this.arcGood.push(f.line(this.dynamic, COLORS.good, 0.05, 0.9));
    for (let i = 0; i < ARC_SEGMENTS; i++)
      this.arcPerfect.push(f.line(this.dynamic, COLORS.perfect, 0.07, 1));
    for (let i = 0; i < ARC_SEGMENTS; i++) {
      this.releaseGood.push(f.line(this.dynamic, COLORS.good, 0.05, 0.5));
      this.releasePerfect.push(f.line(this.dynamic, COLORS.perfect, 0.07, 0.7));
      this.readyZone.push(f.line(this.dynamic, COLORS.perfect, 0.05, 0.45));
    }
    this.rollTrack = f.line(this.dynamic, COLORS.mark, 0.06, 0.6);
    this.rollSpin = f.line(this.dynamic, COLORS.perfect, 0.06, 0.9);
    this.rollMark = f.line(this.dynamic, COLORS.com, 0.03, 1);
    this.capsule = makeCapsule(f, this.dynamic, COLORS.capsule, 1);
    this.capsuleInner = makeCapsule(f, this.dynamic, COLORS.capsuleFill, 1);
    this.flash = f.disc(this.dynamic, 0, 0, 1, COLORS.flash, 0);
    this.com = f.disc(this.dynamic, 0, 0, 0.06, COLORS.com);
    this.facingTick = f.line(this.dynamic, COLORS.facing, 0.03);
    this.armLeft = f.line(this.dynamic, COLORS.arm, 0.04);
    this.armRight = f.line(this.dynamic, COLORS.arm, 0.04);
    this.reachRing = f.ringAt(this.dynamic, 0, 0, 0.3, COLORS.reach, 0.8);
    this.assistLine = f.line(this.dynamic, COLORS.assist, 0.015, 0.7);
  }

  /** Show or hide the character debug shapes (the teaching marks are separate). */
  setDynamicVisible(v: boolean): void {
    this.dynamic.visible = v;
  }

  /** Show or hide the teaching marks (window bands, split marks, respawn points). */
  setTellsVisible(v: boolean): void {
    this.tells.visible = v;
  }

  /** Remove everything this renderer created (a course change builds a new one). Shared textures stay. */
  destroy(): void {
    this.root.parent?.removeChild(this.root);
    this.root.destroy({ children: true, texture: false, textureSource: false });
  }

  draw(view: ViewState, sim: Sim, camera: Camera, feel: RenderFeel, nowMs: number): void {
    if (!this.dynamic.visible) return;
    const t = sim.tuning;
    const world = sim.world;
    const r = t.capsuleRadius;
    const x = view.x;
    const y = view.y;
    const h = view.height;
    const absorb = view.absorb;

    // Body axis: rotate the capsule about the center of mass.
    const ux = Math.sin(view.bodyAngle + view.torsoLean);
    const uy = Math.cos(view.bodyAngle + view.torsoLean);
    const cx = x;
    const cy = y + view.comY - absorb * 0.5;
    const footD = view.comY - r;
    const headD = h - view.comY - r - absorb;
    // Capsule outline (an inner capsule in the fill color leaves a rim).
    const rim = 0.025;
    setCapsule(this.capsule, cx - ux * footD, cy - uy * footD, cx + ux * headD, cy + uy * headD, r);
    setCapsule(
      this.capsuleInner,
      cx - ux * footD,
      cy - uy * footD,
      cx + ux * headD,
      cy + uy * headD,
      r - rim,
    );
    const failing = view.state === S.Fail;
    setCapsuleTint(this.capsule, failing ? COLORS.water : COLORS.capsule);

    setDisc(this.com, cx, cy, 0.06);
    const fb = view.facingBlend;
    setLine(this.facingTick, cx, cy, cx + fb * (r + 0.15), cy);

    // Arms: from the (rotated) shoulders to the hand point; red when out.
    const armColor = view.armsOut === 1 ? COLORS.armOut : COLORS.arm;
    this.armLeft.tint = armColor;
    this.armRight.tint = armColor;
    const shD = view.shoulderH - view.comY;
    const sx = cx + ux * shD;
    const sy = cy + uy * shD;
    setLine(this.armLeft, sx - 0.06 * uy, sy + 0.06 * ux, view.handX - 0.03, view.handY);
    setLine(this.armRight, sx + 0.06 * uy, sy - 0.06 * ux, view.handX + 0.03, view.handY);

    // Reach radius around the hand while the arms are out and free.
    const showReach = view.armsOut === 1 && view.barId < 0;
    this.reachRing.visible = showReach;
    if (showReach) setDisc(this.reachRing, view.handX, view.handY, view.reachRadius);

    // Assisted target bar.
    const assist = view.assistBar;
    this.assistLine.visible = assist >= 0 && view.barId < 0;
    if (assist >= 0)
      setLine(this.assistLine, view.handX, view.handY, world.barX[assist]!, world.barY[assist]!);

    // Pendulum, bottom marker, pump window arcs.
    const attached = view.barId >= 0;
    this.pendulum.visible = attached;
    this.bottomMarker.visible = attached;
    for (let i = 0; i < ARC_SEGMENTS; i++) {
      this.arcGood[i]!.visible = attached;
      this.arcPerfect[i]!.visible = attached;
      this.releaseGood[i]!.visible = attached;
      this.releasePerfect[i]!.visible = attached;
    }
    const DEG = Math.PI / 180;
    if (attached) {
      // A ladder's bar is in flight between rungs during a pop. Read the same interpolated
      // anchor as the hands and world bar so the debug pendulum does not jump ahead of them.
      const bx = view.handX;
      const by = view.handY;
      const L = view.L;
      setLine(this.pendulum, bx, by, x, y + view.comY);
      this.bottomMarker.position.set(bx - 0.02, by - L - 0.3);
      const omega = Math.abs(view.omega);
      const goodHalf = Math.max(0.02, omega * (t.pumpGoodMs / 1000));
      const perfectHalf = Math.max(0.01, omega * (t.pumpPerfectMs / 1000));
      // The pump window is centered pumpLeadMs before the crossing, on the side the swing comes from.
      const lead = -Math.sign(view.omega) * omega * (t.pumpLeadMs / 1000);
      this.drawArc(this.arcGood, bx, by, L + 0.12, lead - goodHalf, lead + goodHalf);
      this.drawArc(this.arcPerfect, bx, by, L + 0.12, lead - perfectHalf, lead + perfectHalf);
      // Release wedges on the forward side (the facing the catch set).
      const side = view.facing;
      const g0 = t.releaseGoodMin * DEG * side;
      const g1 = t.releaseGoodMax * DEG * side;
      const p0 = t.releasePerfectMin * DEG * side;
      const p1 = t.releasePerfectMax * DEG * side;
      this.drawArc(this.releaseGood, bx, by, L + 0.3, Math.min(g0, g1), Math.max(g0, g1));
      this.drawArc(this.releasePerfect, bx, by, L + 0.3, Math.min(p0, p1), Math.max(p0, p1));
    }

    // Catch-ready zone on the nearest bar ahead within reach of a flight: the arc of mass positions
    // catchReadyDeg and further behind the bar at the hanging length, where a grab swings forward.
    let next = -1;
    let nextD = 7;
    if (!attached) {
      for (let i = 0; i < world.barCount; i++) {
        const dx = (world.barX[i]! - x) * view.facing;
        if (dx > -0.5 && dx < nextD && i !== view.lastBarId) {
          nextD = dx;
          next = i;
        }
      }
    }
    for (let i = 0; i < ARC_SEGMENTS; i++) this.readyZone[i]!.visible = next >= 0;
    if (next >= 0) {
      const a0 = -t.catchReadyDeg * DEG * view.facing;
      const a1 = -80 * DEG * view.facing;
      this.drawArc(
        this.readyZone,
        world.barX[next]!,
        world.barY[next]!,
        restLength(t, 0),
        Math.min(a0, a1),
        Math.max(a0, a1),
      );
    }

    // Roll tell: a horizontal gauge beside the body while airborne. The fill is the forward spin (in
    // the direction of travel), the red tick the spin the legs absorb at the current tuck; past it, a
    // touchdown rolls.
    const airborne = view.grounded === 0 && view.barId < 0;
    this.rollTrack.visible = airborne;
    this.rollSpin.visible = airborne;
    this.rollMark.visible = airborne;
    if (airborne) {
      const gx = cx - 0.6;
      const gy = cy + 1.2;
      const scale = 0.06; // m of gauge per rad/s
      const dir = view.vx >= 0 ? 1 : -1;
      const spin = Math.max(0, view.phiDot * dir);
      const limit = rollThreshold(t, view.tuck);
      setLine(this.rollTrack, gx, gy, gx + t.rollSpinUntucked * scale * 1.2, gy);
      setLine(
        this.rollSpin,
        gx,
        gy,
        gx + Math.max(0.001, Math.min(spin, t.rollSpinUntucked * 1.2) * scale),
        gy,
      );
      this.rollSpin.tint = spin > limit ? COLORS.perfect : COLORS.good;
      setLine(this.rollMark, gx + limit * scale, gy - 0.08, gx + limit * scale, gy + 0.08);
    }

    // Pump flash: a short white bloom on PERFECT.
    const sinceFlashMs = ((view.tick - view.lastPumpTick) * 1000) / HZ;
    const flashing = view.lastPumpGrade === Grade.Perfect && sinceFlashMs >= 0 && sinceFlashMs < feel.flashMs;
    this.flash.alpha = flashing ? 0.7 * (1 - sinceFlashMs / feel.flashMs) : 0;
    if (flashing) setDisc(this.flash, x, y + view.comY, 0.9 + 0.6 * (sinceFlashMs / feel.flashMs));

    void camera;
    void nowMs;
  }

  /** An arc below the pivot from angle a0 to a1 (radians from straight down, positive toward +x). */
  private drawArc(segments: Sprite[], cx: number, cy: number, radius: number, a0: number, a1: number): void {
    const n = segments.length;
    let prevX = cx + radius * Math.sin(a0);
    let prevY = cy - radius * Math.cos(a0);
    for (let i = 0; i < n; i++) {
      const a = a0 + ((i + 1) / n) * (a1 - a0);
      const px = cx + radius * Math.sin(a);
      const py = cy - radius * Math.cos(a);
      setLine(segments[i]!, prevX, prevY, px, py);
      prevX = px;
      prevY = py;
    }
  }
}
